// Command layer. Pure classifier sitting between
// raw Web MIDI note events and the held-set. Decides, per note event, whether it
// is a *command* (suppressed from the held set) or *music* (passed through), owns
// the command-mode flag and the Hayes-+++-style escape gesture, and emits semantic
// callbacks. No DOM, no Web MIDI, no clock — timestamps are supplied by the caller
// (the MIDI event's timeStamp), so this never becomes a second timebase.

/** Mid-play actions reachable from the always-on live strip. */
export type LiveAction =
  | "restart"
  | "loopCycle"
  | "playPause"
  | "tempoDown"
  | "tempoUp"
  | "toggleCoach"
  | "playForMe"
  | "toggleMetronome"
  | "toggleRecord";

/** Cursor movements emitted while in command mode (exit is handled internally). */
export type NavKey = "up" | "down" | "left" | "right" | "select";

export interface PianoControlConfig {
  /** Whether the always-on live strip fires (false = those keys play as notes). */
  liveKeysEnabled: boolean;
  /** Note that triggers the command-mode escape gesture (C2 = 36). */
  escapeNote: number;
  /** Consecutive taps of `escapeNote` needed to toggle command mode. */
  escapeTaps: number;
  /** Required silence (ms) before the first escape tap counts. */
  idleGuardMs: number;
  /** Max gap (ms) between consecutive escape taps before the sequence resets. */
  tapWindowMs: number;
  /** pitch -> live action (only when `liveKeysEnabled` and not in command mode). */
  liveKeys: ReadonlyMap<number, LiveAction>;
  /** pitch -> nav action (only in command mode); value "exit" toggles mode. */
  navKeys: ReadonlyMap<number, NavKey | "exit">;
}

export interface CommandLayerCallbacks {
  onLiveAction(action: LiveAction): void;
  onEnterCommandMode(): void;
  onExitCommandMode(): void;
  onNav(key: NavKey): void;
}

export const DEFAULT_PIANO_CONTROL: PianoControlConfig = {
  liveKeysEnabled: true,
  escapeNote: 36,
  escapeTaps: 3,
  idleGuardMs: 2000,
  tapWindowMs: 1500,
  // Live strip on a contiguous run from C♯2 up — low enough to stay out of
  // most playing, but reachable (the extreme bottom keys were too far away).
  liveKeys: new Map<number, LiveAction>([
    [37, "toggleCoach"], // C♯2 — master on/off for the continuous coach loop
    [38, "restart"], // D2
    [39, "playForMe"], // D♯2 — "play it for me" (auto listen-through)
    [40, "playPause"], // E2
    [41, "tempoDown"], // F2 — tempo ∓ straddles the metronome…
    [42, "toggleMetronome"], // F♯2 — metronome click on/off
    [43, "tempoUp"], // G2 — …on F2/G2
    [44, "loopCycle"], // G♯2 — A–B loop cycle (moved off D♯2)
    [45, "toggleRecord"], // A2 — record performance → MIDI export
  ]),
  // D-pad on the white keys from C5 up (the octave above middle C), so middle C
  // (60) stays a free, playable key.
  navKeys: new Map<number, NavKey | "exit">([
    [72, "left"], // C5
    [74, "right"], // D5
    [76, "up"], // E5
    [77, "down"], // F5
    [79, "select"], // G5
    [81, "exit"], // A5
  ]),
};

export class CommandLayer {
  private active = false;
  private lastEventMs = Number.NEGATIVE_INFINITY;
  private tapTimes: number[] = [];
  /** Pitches whose note-on we suppressed, so we also suppress their note-off. */
  private readonly suppressed = new Set<number>();

  constructor(
    private cfg: PianoControlConfig,
    private readonly cb: CommandLayerCallbacks,
  ) {}

  get commandMode(): boolean {
    return this.active;
  }

  setConfig(cfg: PianoControlConfig): void {
    this.cfg = cfg;
  }

  /**
   * Feed a raw note event. Returns true if it must be suppressed from the held
   * set (a command / muted-in-command-mode note), false to let it play.
   */
  note(pitch: number, on: boolean, timeMs: number): boolean {
    const idleBefore = timeMs - this.lastEventMs;
    this.lastEventMs = timeMs;

    if (!on) {
      if (this.suppressed.delete(pitch)) return true;
      return false;
    }

    if (pitch === this.cfg.escapeNote && this.advanceEscape(timeMs, idleBefore)) {
      return true; // a counted escape tap is always suppressed
    }

    if (this.active) {
      const nav = this.cfg.navKeys.get(pitch);
      if (nav === "exit") {
        this.active = false;
        this.cb.onExitCommandMode();
      } else if (nav !== undefined) {
        this.cb.onNav(nav);
      }
      // Do NOT suppress: let the key reach the held set so it still highlights
      // (the engine is paused in command mode, so nothing is scored). This keeps
      // physical-key alignment visible while the dialog is open.
      return false;
    }

    if (this.cfg.liveKeysEnabled) {
      const action = this.cfg.liveKeys.get(pitch);
      if (action !== undefined) {
        this.suppressed.add(pitch);
        this.cb.onLiveAction(action);
        return true;
      }
    }
    return false;
  }

  /**
   * Update the escape-tap sequence for an `escapeNote` press. Returns true if the
   * press was counted as part of a (possibly completing) gesture — i.e. suppress it.
   */
  private advanceEscape(timeMs: number, idleBefore: number): boolean {
    if (this.tapTimes.length === 0) {
      if (idleBefore < this.cfg.idleGuardMs) return false; // not idle enough to start
      this.tapTimes = [timeMs];
    } else {
      const last = this.tapTimes[this.tapTimes.length - 1] as number;
      if (timeMs - last > this.cfg.tapWindowMs) {
        // sequence broke; this press may itself start a fresh sequence
        this.tapTimes = idleBefore >= this.cfg.idleGuardMs ? [timeMs] : [];
        if (this.tapTimes.length === 0) return false;
      } else {
        this.tapTimes.push(timeMs);
      }
    }

    if (this.tapTimes.length >= this.cfg.escapeTaps) {
      this.tapTimes = [];
      this.active = !this.active;
      if (this.active) this.cb.onEnterCommandMode();
      else this.cb.onExitCommandMode();
    }
    return true;
  }
}
